import { Currency, Token } from "@uniswap/sdk-core";
import { useWeb3React } from "@web3-react/core";
import { getChainInfo } from "constants/chainInfo";
import { SupportedChainId } from "constants/chains";
import {
  DEFAULT_INACTIVE_LIST_URLS,
  DEFAULT_LIST_OF_LISTS,
} from "constants/lists";
import {
  useCurrencyFromMap,
  useTokenFromMapOrNetwork,
} from "lib/hooks/useCurrency";
import { getTokenFilter } from "lib/hooks/useTokenList/filtering";
import { useMemo } from "react";
import { isL2ChainId } from "utils/chains";

import {
  useAllLists,
  useCombinedActiveList,
  useCombinedTokenMapFromUrls,
} from "../state/lists/hooks";
import { TokenAddressMap, useUnsupportedTokenList } from "../state/lists/hooks";
import { WrappedTokenInfo } from "../state/lists/wrappedTokenInfo";
import {
  useUserAddedTokens,
  useUserAddedTokensOnChain,
} from "../state/user/hooks";

// reduce token map into standard address <-> Token mapping, optionally include user added tokens
function useTokensFromMap(tokenMap: TokenAddressMap): {
  [address: string]: Token;
} {
  const { chainId } = useWeb3React();
  return useMemo(() => {
    const effectiveChainId = chainId || SupportedChainId.HAUST_TESTNET;
    // reduce to just tokens
    return Object.keys(tokenMap[effectiveChainId] ?? {}).reduce<{
      [address: string]: Token;
    }>((newMap, address) => {
      newMap[address] = tokenMap[effectiveChainId][address].token;
      return newMap;
    }, {});
  }, [chainId, tokenMap]);
}

export function useAllTokensMultichain(): TokenAddressMap {
  return useCombinedTokenMapFromUrls(DEFAULT_LIST_OF_LISTS);
}

// Returns all tokens from the default list + user added tokens
export function useDefaultActiveTokens(forPools?: boolean): {
  [address: string]: Token;
} {
  const defaultListTokens = useCombinedActiveList();
  const tokensFromMap = useTokensFromMap(defaultListTokens);
  const userAddedTokens = useUserAddedTokens();

  return useMemo(() => {
    // First filter out MYR tokens from tokensFromMap
    const filteredTokensFromMap = forPools
      ? tokensFromMap
      : Object.entries(tokensFromMap).reduce((acc, [address, token]) => {
          if (
            token.symbol !== "MYR" &&
            token.symbol?.toLocaleUpperCase() !== "WHAUST"
          ) {
            acc[address] = token;
          }
          return acc;
        }, {} as { [address: string]: Token });
    return (
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (tokenMap, token) => {
            // Only add tokens that are not MYR
            if (
              (token.symbol !== "MYR" &&
                token.symbol?.toLocaleUpperCase() !== "WHAUST") ||
              !forPools
            ) {
              tokenMap[token.address] = token;
            }
            return tokenMap;
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          { ...filteredTokensFromMap }
        )
    );
  }, [tokensFromMap, userAddedTokens]);
}

type BridgeInfo = Record<
  SupportedChainId,
  {
    tokenAddress: string;
    originBridgeAddress: string;
    destBridgeAddress: string;
  }
>;

export function useUnsupportedTokens(): { [address: string]: Token } {
  const { chainId } = useWeb3React();
  const listsByUrl = useAllLists();
  const unsupportedTokensMap = useUnsupportedTokenList();
  const unsupportedTokens = useTokensFromMap(unsupportedTokensMap);

  // checks the default L2 lists to see if `bridgeInfo` has an L1 address value that is unsupported
  const l2InferredBlockedTokens: typeof unsupportedTokens = useMemo(() => {
    if (!chainId || !isL2ChainId(chainId)) {
      return {};
    }

    if (!listsByUrl) {
      return {};
    }

    const listUrl = getChainInfo(chainId)?.defaultListUrl || "";

    const { current: list } = listsByUrl[listUrl];
    if (!list) {
      return {};
    }

    const unsupportedSet = new Set(Object.keys(unsupportedTokens));

    return list.tokens.reduce((acc, tokenInfo) => {
      const bridgeInfo = tokenInfo.extensions
        ?.bridgeInfo as unknown as BridgeInfo;
      if (
        bridgeInfo &&
        bridgeInfo[SupportedChainId.HAUST] &&
        bridgeInfo[SupportedChainId.HAUST].tokenAddress &&
        unsupportedSet.has(bridgeInfo[SupportedChainId.HAUST].tokenAddress)
      ) {
        const address = bridgeInfo[SupportedChainId.HAUST].tokenAddress;
        // don't rely on decimals--it's possible that a token could be bridged w/ different decimals on the L2
        return {
          ...acc,
          [address]: new Token(
            SupportedChainId.HAUST,
            address,
            tokenInfo.decimals
          ),
        };
      }
      return acc;
    }, {});
  }, [chainId, listsByUrl, unsupportedTokens]);

  return { ...unsupportedTokens, ...l2InferredBlockedTokens };
}

export function useSearchInactiveTokenLists(
  search: string | undefined,
  minResults = 10
): WrappedTokenInfo[] {
  const lists = useAllLists();
  const inactiveUrls = DEFAULT_INACTIVE_LIST_URLS;
  const { chainId } = useWeb3React();
  const activeTokens = useDefaultActiveTokens();
  return useMemo(() => {
    if (!search || search.trim().length === 0) return [];
    const tokenFilter = getTokenFilter(search);
    const result: WrappedTokenInfo[] = [];
    const addressSet: { [address: string]: true } = {};
    for (const url of inactiveUrls) {
      const list = lists[url]?.current;
      if (!list) continue;
      for (const tokenInfo of list.tokens) {
        if (tokenInfo.chainId === chainId && tokenFilter(tokenInfo)) {
          try {
            const wrapped: WrappedTokenInfo = new WrappedTokenInfo(
              tokenInfo,
              list
            );
            if (
              !(wrapped.address in activeTokens) &&
              !addressSet[wrapped.address]
            ) {
              addressSet[wrapped.address] = true;
              result.push(wrapped);
              if (result.length >= minResults) return result;
            }
          } catch {
            continue;
          }
        }
      }
    }
    return result;
  }, [activeTokens, chainId, inactiveUrls, lists, minResults, search]);
}

// Check if currency is included in custom list from user storage
export function useIsUserAddedToken(
  currency: Currency | undefined | null
): boolean {
  const userAddedTokens = useUserAddedTokens();

  if (!currency) {
    return false;
  }

  return !!userAddedTokens.find((token) => currency.equals(token));
}

// Check if currency on specific chain is included in custom list from user storage
export function useIsUserAddedTokenOnChain(
  address: string | undefined | null,
  chain: number | undefined | null
): boolean {
  const userAddedTokens = useUserAddedTokensOnChain(chain);

  if (!address || !chain) {
    return false;
  }

  return !!userAddedTokens.find((token) => token.address === address);
}

// undefined if invalid or does not exist
// null if loading or null was passed
// otherwise returns the token
export function useToken(
  tokenAddress?: string | null,
  forPools?: boolean
): Token | null | undefined {
  const tokens = useDefaultActiveTokens(forPools);
  return useTokenFromMapOrNetwork(tokens, tokenAddress);
}

export function useCurrency(
  currencyId?: string | null
): Currency | null | undefined {
  const tokens = useDefaultActiveTokens();
  return useCurrencyFromMap(tokens, currencyId);
}
